/* eslint-disable @typescript-eslint/no-unnecessary-condition -- defensive reads of optional projection fields */
import { useState } from 'preact/hooks';
import { useStore } from '../store';

/** P215 deep studbook panel (Office > Studbook). Enable toggle dispatches
 *  studbook.set; the deep numbers come from the ui.studbook projection
 *  (Sim-Core PR #389): per-species herd mean kinship, founder representation,
 *  and the recommended pairing that minimizes kinship. The family book view
 *  reuses the ui.mgmt.breeding pedigree registry like PedigreePanel.
 *  PLACEHOLDER (flagged in sim): recommendation eligibility bars are design
 *  estimates (STUDBOOK_RULES). Reads are defensive until the bridge fields
 *  land on every client. */

interface BreedingPedigree {
  pedigree: Record<string, { sire: string | null; dam: string | null }>;
}

const fmtBp = (bp: number) => `${(bp / 100).toFixed(2)}%`;
const fmtSpecies = (id: string) => id.replace(/_/g, ' ');

export function StudbookPanel() {
  const { sim, bridge } = useStore();
  const studbook = sim.studbook;
  const breeding = (sim as unknown as { breeding?: BreedingPedigree }).breeding;
  const [openSpecies, setOpenSpecies] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const nameOf = (id: string | null | undefined) => {
    if (!id) return 'Unknown';
    const a = sim.animals.find((x) => x.id === id);
    return a ? a.name : id;
  };

  if (!studbook) {
    return (
      <div style="padding:6px 14px 16px">
        <div class="eyebrow eyebrow-rule">Studbook</div>
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:8px">
          Mean kinship, founder representation and recommended pairings appear once the studbook projection lands (Sim-Core #389).
        </p>
      </div>
    );
  }

  if (!studbook.enabled) {
    return (
      <div style="padding:6px 14px 16px">
        <div class="eyebrow eyebrow-rule">Studbook</div>
        <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:10px;padding:12px 14px">
          <div class="row-value" style="font-size:14px">Deep studbook is off</div>
          <p style="font-size:13px;color:var(--ui-ink-soft);margin:6px 0 0">
            Turn it on to track herd mean kinship, founder representation, and get a recommended pairing that minimizes kinship per species.
          </p>
          <button
            type="button"
            class="btn btn-primary"
            style="margin-top:12px;width:100%;min-height:44px"
            onClick={() => { bridge.send('studbook.set', { enabled: true }); }}
          >
            Enable deep studbook
          </button>
          <div class="row-label" style="margin-top:8px;text-transform:none;letter-spacing:0.01em;white-space:normal">
            Recommendations also check current breeding permissions, relatedness, reservations, hybrid fertility and cross-pen housing. No animals are paired automatically.
          </div>
        </div>
      </div>
    );
  }

  const species = studbook.species ?? [];
  const pedigree = breeding?.pedigree ?? {};

  return (
    <div style="padding:6px 14px 16px">
      <div class="eyebrow eyebrow-rule">Studbook - deep mode</div>

      <button
        type="button"
        class="btn btn-quiet"
        style="margin-top:10px;width:100%;min-height:44px"
        onClick={() => { bridge.send('studbook.set', { enabled: false }); }}
      >
        Turn deep studbook off
      </button>

      {species.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:10px">No animals on the reserve yet - the book starts with your first arrivals.</p>
      )}

      {species.map((sp) => {
        const open = openSpecies === sp.species;
        const rec = sp.recommended;
        return (
          <div key={sp.species} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:10px;padding:10px 12px">
            <button
              type="button"
              style="all:unset;display:flex;width:100%;cursor:pointer;min-height:44px;align-items:center"
              onClick={() => { setOpenSpecies(open ? null : sp.species); setOpenId(null); }}
              aria-expanded={open}
            >
              <span class="row-main">
                <div class="row-value" style="font-size:14px;text-transform:capitalize">{fmtSpecies(sp.species)}</div>
                <div class="row-label">herd mean kinship {fmtBp(sp.herdMeanKinshipBp)}</div>
              </span>
              <span class="row-meta">{open ? '▾' : '▸'}</span>
            </button>

            {open && (
              <div style="margin-top:8px;border-top:var(--ui-border);padding-top:8px">
                <div class="row-label">Recommended pairing (minimizes kinship)</div>
                {rec ? (
                  <div class="card" style="border:var(--ui-border);background:var(--ui-parchment);margin-top:6px;padding:8px 10px">
                    <div class="row-value" style="font-size:14px">{nameOf(rec.sireId)} <span style="font-weight:400;color:var(--ui-ink-soft)">× {nameOf(rec.damId)}</span></div>
                    <div class="row-label" style="margin-top:2px">expected kinship {fmtBp(rec.kinshipBp)}</div>
                  </div>
                ) : (
                  <p style="font-size:13px;color:var(--ui-ink-soft);margin:6px 0 0">No eligible pair right now. Recommendations require mature, healthy, calm stock and the current balanced-pair command checks, including permissions, relatedness, reservations, hybrid fertility and housing.</p>
                )}

                <div class="row-label" style="margin-top:12px">Founder representation</div>
                {sp.founders.length === 0 && (
                  <p style="font-size:13px;color:var(--ui-ink-soft);margin:6px 0 0">No recorded founders for this species.</p>
                )}
                {sp.founders.map((f) => (
                  <div key={f.founderId} class="row" style="margin:0;border:none;padding:4px 0">
                    <span class="row-main"><div class="row-value" style="font-size:13px">{nameOf(f.founderId)}</div></span>
                    <span class="row-meta" style="font-size:12px">{f.descendants} living</span>
                  </div>
                ))}

                <div class="row-label" style="margin-top:12px">Family book</div>
                {sim.animals.filter((a) => fmtSpecies(a.species).toLowerCase() === fmtSpecies(sp.species).toLowerCase()).map((a) => {
                  const recPed = pedigree[a.id];
                  const expanded = openId === a.id;
                  return (
                    <div key={a.id} class="card" style="border:var(--ui-border);background:var(--ui-parchment);margin-top:6px;padding:8px 10px">
                      <button
                        type="button"
                        style="all:unset;display:flex;width:100%;cursor:pointer;min-height:44px;align-items:center"
                        onClick={() => { setOpenId(expanded ? null : a.id); }}
                        aria-expanded={expanded}
                      >
                        <span class="row-main">
                          <div class="row-value" style="font-size:13px">{a.name}</div>
                          <div class="row-label">{a.sex}{recPed && (recPed.sire || recPed.dam) ? '' : ' - founder'}</div>
                        </span>
                        <span class="row-meta">{expanded ? '▾' : '▸'}</span>
                      </button>
                      {expanded && (
                        <div style="margin-top:6px;border-top:var(--ui-border);padding-top:6px;font-size:13px">
                          <div><span class="row-label" style="display:inline">Sire</span> {nameOf(recPed?.sire)}</div>
                          <div style="margin-top:4px"><span class="row-label" style="display:inline">Dam</span> {nameOf(recPed?.dam)}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div class="row-label" style="margin-top:12px;text-transform:none;letter-spacing:0.01em;white-space:normal">
        Recommendations also check current breeding permissions, relatedness, reservations, hybrid fertility and cross-pen housing. No animals are paired automatically. Choose animals and request pairing separately from the herd.
      </div>
    </div>
  );
}
